import { api } from "../../../../services/rpc/RpcProvider";
import { useMemo, useState } from "react";
import { StepOverview } from "./JourneyStepRow";
import { Icon, Input, Stack, Title } from "@introist/react-foundation/v2";
import moment, { Moment } from "moment";
import { StepList } from "./StepList";
import { TabBar } from "../../../../components/molecules";
import { orderBy, partition } from "lodash";
import { pendingPreconditionCount } from "../../utils";
import { AutomationStepDrawer } from "../../containers/AutomationStepDrawer";
import { introistDateTimeToMoment } from "../../../../utils/dates";

const filterSteps = (steps: StepOverview[], after?: Moment, before?: Moment) => {
  return steps.filter(step => {
    const scheduledAt = introistDateTimeToMoment(step.start);
    const isAfter = after ? scheduledAt.isAfter(after) : true;
    const isBefore = before ? scheduledAt.isBefore(before) : true;
    return isAfter && isBefore;
  });
};

export const UpcomingSteps = () => {
  const steps = api.journeys.steps.upcoming.useQuery({});

  const [tab, setTab] = useState("upcoming");
  const [search, setSearch] = useState("");

  const [openStep, setOpenStep] = useState<string | undefined>();

  const filteredSteps = useMemo(() => {
    if (!steps.data) return [] as StepOverview[];

    const [paused, upcoming] = partition(
      orderBy(steps.data, s => introistDateTimeToMoment(s.start).toISOString()),
      step => pendingPreconditionCount(step as StepOverview) > 0
    );

    const failing = upcoming.filter(step => step.status === "failing");

    const outdated = upcoming.filter(
      step => step.workflowStepVersion !== step.currentWorkflowStepVersion
    );

    const stepsMap: { [tabKey: string]: any[] } = {
      upcoming: upcoming,
      failing: failing,
      paused: paused,
      outdated: outdated
    };

    const tabSteps = stepsMap[tab] || [];

    if (!search) return tabSteps as StepOverview[];

    return tabSteps.filter(step => {
      return (
        step.title.toLowerCase().includes(search.toLowerCase()) ||
        step.employee?.name.toLowerCase().includes(search.toLowerCase())
      );
    }) as StepOverview[];
  }, [steps.data, tab, search]);

  const lateSteps = useMemo(() => {
    return filterSteps(filteredSteps, undefined, moment().startOf("isoWeek"));
  }, [filteredSteps]);

  const thisWeek = useMemo(() => {
    return filterSteps(filteredSteps, moment().startOf("isoWeek"), moment().endOf("isoWeek"));
  }, [filteredSteps]);

  const nextWeek = useMemo(() => {
    return filterSteps(
      filteredSteps,
      moment().endOf("isoWeek"),
      moment().add(1, "week").endOf("isoWeek")
    );
  }, [filteredSteps]);

  const later = useMemo(() => {
    return filterSteps(filteredSteps, moment().add(1, "week").endOf("isoWeek"), undefined);
  }, [filteredSteps]);

  return (
    <Stack vertical>
      <Stack justifyContent="space-between">
        <TabBar
          tabs={[
            { id: "upcoming", title: "All" },
            { id: "failing", title: "Failing" },
            { id: "paused", title: "Paused" },
            { id: "outdated", title: "Outdated" }
          ]}
          activeTab={tab}
          onTabChange={setTab}
        />
        <Stack>
          <Title small>{steps.data ? `${filteredSteps.length} steps` : ""}</Title>
          <Input
            size="small"
            placeholder="Search"
            endAdornment={<Icon name="search" subdued />}
            style={{ width: "250px" }}
            value={search}
            onChange={setSearch}
          />
        </Stack>
      </Stack>

      <StepList
        loading={!steps.data || steps.isLoading}
        sections={[
          { title: "Late", steps: lateSteps },
          { title: "This week", steps: thisWeek },
          { title: "Next week", steps: nextWeek },
          { title: "After next week", steps: later }
        ]}
        onClick={stepId => setOpenStep(stepId)}
      />
      <AutomationStepDrawer
        open={!!openStep}
        stepId={openStep}
        onClose={() => setOpenStep(undefined)}
        enableJourneyNavigate
      />
    </Stack>
  );
};
